import type { Locales } from '../composables/i18n';
import { useCommonStore } from '~/stores/common';
import { i18nGlobal } from '~/utils/i18n';
import { router as globalRouter, routeMap } from '~/utils/router';
import { commonRequests } from '~/requests/common/common.request';
import { resHandler } from '~/utils/http/handler';
import { globalMessage } from '~/utils/discreteApi';
import type { CreateAndUpdateDateProps } from '~/requests/common/types';

export type Dept = {
    deptId: number;
    parentId: number;
    deptLevels: string;
    deptName: string;
    orderNum: number;
    leader: string;
    phone: string;
    email: string;
    // 1正常0停用
    status: string;
    parentName: string;
    children: Dept[];
    // dict 1008
    deptType: string;
    orgTax: number;
} & CreateAndUpdateDateProps;

export type UserLangListItem = {
    langCode: Locales;
    langName: string;
};

export type Role = {
    roleId: number;
    roleName: string;
    roleKey: string;
    roleSort: number;
    // 数据范围（1：所有数据权限；2：自定义数据权限；3：本部门数据权限；4：本部门及以下数据权限；5：仅本人数据权限）
    dataScope: string;
    // 菜单树选择项是否关联显示（ 0：父子不互相关联显示 1：父子互相关联显示）
    menuCheckStrictly: boolean;
    // 部门树选择项是否关联显示（0：父子不互相关联显示 1：父子互相关联显示 ）
    deptCheckStrictly: boolean;
    // 1正常0停用
    status: number;
    // 0代表存在 1代表删除
    delFlag: number;
    flag: boolean;
    menuIds: number[];
    deptIds: number[];
    permissions: string[];
    remark: string;
    // 区分下系统1->POMS,2->SNW
    sysId: number;
} & CreateAndUpdateDateProps;

export type UserInfo = {
    deptType: string;
    userid: number;
    deptId: number;
    username: string;
    realName: string;
    email: string;
    phoneNumber: string;
    sex: string;
    avatar: string;
    dept: Dept;
    roles: Role[];
    roleIds: number[];
    postIds: number[];
    roleId: number;
    companyId: any;
    customerId: number;
    remark: string;
    childrenDeptIdList: number[];
    areaCode: number;
    // 区分下系统1->POMS,2->SNW
    sysId: number;
    idaasUser: number;
    pwdRemindDay: string;
    userTimeZone: string;
    portalUsername: string;
} & CreateAndUpdateDateProps;

export type UploadProgressListItem = { name: string; percentage: number; id: number };

const useUploadProgress = () => {
    const uploadItemIndex = ref(0);
    const showUploadProgress = ref(false);
    const uploadList = ref<UploadProgressListItem[]>([]);

    return {
        uploadItemIndex,
        uploadList,
        showUploadProgress,
    };
};

export const useUserStore = defineStore('user', () => {
    const commonStore = useCommonStore();
    const instanceRouter = useRouter();
    const { availableLocales } = useAvailableLocaleList();
    const companyCountry = ref<any>(null);
    const userInfo = ref<UserInfo | null>(null);
    const permissions = ref<any>(null);
    const nowPath = ref<string>('');
    const uploadProgress = useUploadProgress();
    const switchRoleCode: Ref<string | null> = ref(null);
    const setNowPath = (v) => {
        nowPath.value = v;
    };
    const getNowPath = () => {
        return nowPath.value;
    };
    const logoutCb = async () => {
        const router = instanceRouter ?? globalRouter;
        console.log(instanceRouter ? 'instanceRouter' : 'globalRouter');
        routeMap.clear();
        commonStore.formattedRoutes = [];
        const sysid = userInfo.value?.sysId;
        userInfo.value = null;
        await commonStore.clearAuth();
        if (sysid === 2) {
            window.location.href = import.meta.env.VITE_IDP_LOGOUT_URL;
        }
        if (sysid !== 2) await router.push('/auth/login');
        return;
        return globalMessage.success(i18nGlobal.t('auth.logout_success'));
    };

    const logout = async () => {
        if (!commonStore.authToken) {
            await logoutCb();
            return;
        }
        const res = await commonRequests.logout();
        const isSuccess = await resHandler(res);
        if (isSuccess) {
            await logoutCb();
        }
    };

    const getUserInfo = async (flag = false) => {
        const router = instanceRouter ?? globalRouter;

        if (!userInfo.value || flag) {
            // commonRequests.cdcAuth();
            return commonRequests.getUserInfo().then(async (res) => {
                const formatTreeData = (data) => {
                    if (!data || !Array.isArray(data)) return null;

                    return data.map((item) => ({
                        ...item,
                        list: formatTreeData(item.children),
                    }));
                };
                await resHandler(res, {
                    onSuccess: ({ res }) => {
                        companyCountry.value = formatTreeData(res.companyCountry);
                        userInfo.value = res.user;
                        permissions.value = res.permissions;
                        userInfo.value.pwdRemindDay = res.pwdRemindDay;
                        (i18nGlobal.locale.value as any) = res.userLang ? res.userLang : 'en-US';
                        (commonStore.locale as any) = res.userLang ? res.userLang : 'en-US';
                        availableLocales.value = res.userLangList;
                        // if (userInfo.value.idaasUser === 1) commonRequests.cdcAuth();
                    },
                });
            });
        }
    };

    const companyCountryValue = computed(() => userInfo.value?.companyId || null);
    const countryMap = {
        '470007': 'DE',
        '470006': 'FR',
        '430018': 'ES',
        '420006': 'CEE',
        '420208': 'AU',
        '420005': 'AS',
        '430019': 'IT',
        '470018': 'PL',
        '430020': 'PT',
        '420004': 'NL',
        '426006': 'HU',
    };
    const countryCurrentCode = computed(() =>
        companyCountryValue.value ? countryMap[String(companyCountryValue.value)] || '' : '',
    );

    return {
        logout,
        logoutCb,
        permissions,
        userInfo,
        getUserInfo,
        setNowPath,
        getNowPath,
        countryCurrentCode,
        companyCountry,
        switchRoleCode,
        ...uploadProgress,
    };
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
